import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Ticket, UserPlus, X, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";

export default function CourseSeats() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [emails, setEmails] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { api.get("/me/course-seats").then((r) => setPacks(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    load();
  }, [user, loading, navigate, load]);

  if (loading || !user) return <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  const assign = async (id) => {
    const email = (emails[id] || "").trim();
    if (!email) return;
    setBusy(true);
    try { await api.post(`/me/course-seats/${id}/assign`, { email }); toast.success(`Seat assigned to ${email}`); setEmails({ ...emails, [id]: "" }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not assign"); }
    finally { setBusy(false); }
  };
  const unassign = async (id, email) => { await api.delete(`/me/course-seats/${id}/assign/${encodeURIComponent(email)}`); toast.success("Seat freed"); load(); };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center gap-3 mb-8">
        <span className="grid place-items-center w-12 h-12 rounded-xl bg-blue-900 text-teal-400"><Ticket className="w-6 h-6" /></span>
        <div><h1 className="font-display text-3xl font-bold text-blue-950">Course Seats</h1><p className="text-slate-500">Assign purchased training seats to your team by email.</p></div>
      </div>

      {packs.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-blue-900/5">
          <GraduationCap className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No seat packs yet. Buy training seats to assign them to your staff.</p>
          <Button asChild className="mt-4 bg-teal-600 hover:bg-teal-700 text-white"><Link to="/training">Browse Training Marketplace</Link></Button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="seat-packs">
          {packs.map((p) => (
            <div key={p.id} data-testid={`seat-pack-${p.id}`} className="bg-white rounded-2xl border border-blue-900/5 shadow-sm p-5">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-display font-bold text-blue-950">{p.course_title}</h3>
                <span className="text-sm text-slate-500">{p.available} of {p.total_seats} seats available</span>
              </div>
              <div className="mt-3 flex gap-2">
                <Input data-testid={`seat-email-${p.id}`} value={emails[p.id] || ""} onChange={(e) => setEmails({ ...emails, [p.id]: e.target.value })} placeholder="teammate@email.com" className="max-w-xs" />
                <Button data-testid={`seat-assign-${p.id}`} onClick={() => assign(p.id)} disabled={busy || p.available <= 0} className="bg-blue-900 hover:bg-blue-950 text-white gap-1.5"><UserPlus className="w-4 h-4" /> Assign</Button>
              </div>
              {p.assigned.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {p.assigned.map((a) => (
                    <span key={a.email} className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${a.status === "claimed" ? "bg-teal-100 text-teal-800" : "bg-slate-100 text-slate-600"}`}>{a.email}
                      <span className="text-[10px] uppercase font-semibold tracking-wide">{a.status === "claimed" ? "claimed" : "invited"}</span>
                      <button onClick={() => unassign(p.id, a.email)} data-testid={`seat-remove-${p.id}-${a.email}`} className="hover:text-red-500"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
